import { cj } from 'src/utils/classNames';
import {
	BASE_PRECISION_EXP,
	BigNum,
	MarketType,
	ZERO,
	QUOTE_PRECISION_EXP,
	BN,
} from '@drift-labs/sdk';
import { UIMarket, UIOrderType } from '@drift/common';
import { TradeFormDerivedState } from 'src/@types/trade';
import { SaveTpSlOrdersCallback } from '../TpSlOrderForm/types';
import {
	BLUR_OVERLAY_SIBLING_CLASSES,
	PERP_ORDER_TYPE_OPTIONS,
} from '../consts';
import BuySellToggle from 'src/components/Buttons/BuySellToggle';
import { PerpTradeFormPrimaryButton } from '../TradeFormPieces';
import PerpTradeDetails from '../TradeDetails/PerpTradeDetails';
import PriceDivergenceWarningMessage from 'src/components/PriceDivergenceWarningMessage';
import Checkbox from 'src/components/primitives/Checkbox/Checkbox';
import { ITradeForm } from 'src/stores/DriftStore/types/tradeForm';
import SwiftEnableMessage from '../SwiftEnableMessage';
import MaxSizeLabel from '../MaxSizeLabel';
import PriceInput from '../PriceInput/PriceInput';
import PerpLeverageSliderInput from 'src/components/TradeForm/LeverageSliderInput/PerpLeverageSliderInput';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import ScaledOrderCountInput from '../ScaledOrderCountInput/ScaledOrderCountInput';
import SizeInput from '../SizeInput/SizeInput';
import { InvariantChecker } from 'src/utils/UIInvariants/InvariantChecker';
import { formatLeverageLabel } from 'src/utils/formatting';
import { roundToStepSizeIfLargeEnough } from 'src/utils/math';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { useCallback, useMemo } from 'react';
import CollapsibleTpSlOrderForm from '../TpSlOrderForm/CollapsibleTpSlOrderForm';
import OrderTypeBottomDrawerSelect from 'src/components/TradeForm/BottomDrawerSelectOrderType';
import BottomDrawerSelectSizeDistribution from 'src/components/TradeForm/BottomDrawerSelectSizeDistribution';
import ApyBanners from 'src/components/ApyBanners/ApyBanners';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { useCurrentAccountMarginInfo } from 'src/hooks/query/accountTradingData/useMarginInfo';
import Button from 'src/components/primitives/Button/Button';
import { ChevronDown } from '@drift-labs/icons';
import OrderTypeTabs from 'src/components/TradeForm/OrderTypeTabs';
import RadioGroup from 'src/components/primitives/RadioGroup/RadioGroup';
import Slider from 'src/components/primitives/Slider/Slider';
import useModal from 'src/components/Modals/hooks/useModal';
import useGetDisplayLeverageForMarket from 'src/hooks/useGetDisplayLeverageForMarket';
import useMarketSettingsStore from 'src/stores/useMarketSettingsStore';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import Link from 'src/components/primitives/Link/Link';
import { TradeFormHandleSizeInputChange } from 'src/components/TradeForm/types';
import { useWindowSizeIsLessThan } from 'src/hooks/useWindowSize';

interface ProModePerpTradeFormProps {
	selectedUiMarket: UIMarket;
	tradeFormStoreState: ITradeForm;
	tradeFormDerivedState: TradeFormDerivedState;
	isSliderDisabled: boolean;
	priceBoxStringValue: string;
	formattedMarketSymbol: string;
	currentPositionBaseSize: BigNum;
	rawBaseValue: BigNum;
	rawQuoteValue: number;
	totalCurrentPositionSize: number;
	currentPositionSize: number;
	isSameSideAsCurrentPosition: boolean;
	hasAgreedToPriceDivergence: boolean;
	limitPriceVsTriggerPriceWarning: string;
	shouldShowSwiftEnableMessage: boolean;
	currentMarketSymbol: string;
	hasNoCollateral: boolean;
	isTradeButtonDisabled: boolean;
	totalMarginRequired?: BN;
	crossMarginErrorMessage?: string;
	maxMarginRatio: number;
	secondaryPriceBoxStringValue: string;
	baseSizeStringValue: string;
	quoteSizeStringValue: string;
	currentLeverage: number;
	leverageAfterTrade: number;
	entryPriceForTpSl: BigNum;
	showTradeConfirmation: boolean;
	unFocus: () => void;
	onSubmit: () => void;
	onSizeInputChange: TradeFormHandleSizeInputChange;
	onSliderChange: (value: number) => void;
	onInputSourceChange: (inputSource: 'base' | 'quote') => void;
	onDirectionChange: (direction: 'buy' | 'sell') => void;
	onOrderTypeChange: (orderType: UIOrderType) => void;
	onPriceBoxChange: (value: string) => void;
	onSecondaryPriceBoxChange: (value: string) => void;
	onSizeDistributionChange: (
		value: 'flat' | 'ascending' | 'descending'
	) => void;
	saveLimitPrice: (value: string) => void;
	toggleReduceOnly: () => void;
	togglePostOnly: () => void;
	toggleIsTpSlCheckboxChecked: () => void;
	saveBracketOrdersCallback: SaveTpSlOrdersCallback;
	setHasAgreedToPriceDivergence: React.Dispatch<React.SetStateAction<boolean>>;
}

const ORDER_TYPES_WITH_TP_SL = ['market', 'limit'];

export const PerpTradeFormContent = ({
	selectedUiMarket,
	tradeFormDerivedState,
	isSliderDisabled: _isSliderDisabled,
	priceBoxStringValue,
	formattedMarketSymbol,
	currentPositionBaseSize,
	rawBaseValue,
	rawQuoteValue,
	totalCurrentPositionSize,
	currentPositionSize,
	isSameSideAsCurrentPosition,
	hasAgreedToPriceDivergence,
	limitPriceVsTriggerPriceWarning,
	tradeFormStoreState,
	shouldShowSwiftEnableMessage,
	currentMarketSymbol,
	hasNoCollateral,
	isTradeButtonDisabled,
	totalMarginRequired,
	crossMarginErrorMessage,
	maxMarginRatio: _maxMarginRatio,
	secondaryPriceBoxStringValue,
	baseSizeStringValue,
	quoteSizeStringValue,
	currentLeverage,
	leverageAfterTrade: _leverageAfterTrade,
	entryPriceForTpSl,
	showTradeConfirmation,
	unFocus,
	onSubmit,
	onSizeInputChange,
	onSliderChange,
	onDirectionChange,
	onOrderTypeChange,
	onPriceBoxChange,
	onSecondaryPriceBoxChange,
	onSizeDistributionChange,
	onInputSourceChange,
	saveLimitPrice,
	toggleReduceOnly,
	togglePostOnly,
	toggleIsTpSlCheckboxChecked,
	saveBracketOrdersCallback,
	setHasAgreedToPriceDivergence,
}: ProModePerpTradeFormProps) => {
	const isMobileScreenSize = useIsMobileScreenSize();
	const isMdScreenSize = useWindowSizeIsLessThan('md');
	const isLessThanXL = useWindowSizeIsLessThan('xl');

	const needMobileUI = isMdScreenSize;
	const buttonSize = isLessThanXL || needMobileUI ? 'sm' : 'md';
	const isGeoblocked = useDriftStore((s) => s.isGeoblocked);
	const { show } = useModal();

	const { stepSize } = tradeFormDerivedState;
	const { postOnly, reduceOnly, side, scaledOrders } = tradeFormStoreState;
	const sizeDistribution = scaledOrders.sizeDistribution;

	const shouldShowTpSlCheckbox =
		ORDER_TYPES_WITH_TP_SL.includes(tradeFormStoreState.orderType) &&
		!isGeoblocked;

	const baseSizeBigNum = useMemo(
		() => BigNum.fromPrint(baseSizeStringValue, BASE_PRECISION_EXP),
		[baseSizeStringValue]
	);

	const currentUserAccount = useDriftAccountStore((s) =>
		s.getCurrentUserAccount()
	);
	const userKey = currentUserAccount?.userKey;
	const marginInfo = useCurrentAccountMarginInfo();
	const freeCollateral = marginInfo?.freeCollateral ?? ZERO;

	const freeCollateralNum = BigNum.from(
		freeCollateral,
		QUOTE_PRECISION_EXP
	).toNum();

	const displayLeverage = useGetDisplayLeverageForMarket(
		selectedUiMarket.marketIndex
	);

	const openEditPositionMaxLeverage = useCallback(() => {
		show({
			type: 'position_max_leverage',
			props: {
				marketIndex: selectedUiMarket.marketIndex,
			},
		});
	}, [selectedUiMarket.marketIndex, show]);

	const openMarginModeModal = useCallback(() => {
		show({
			type: 'margin_mode',
			props: {},
		});
	}, [show]);

	// Get current margin mode for this market
	const currentMarginMode =
		useMarketSettingsStore(
			(s) =>
				s.getMarginModeForMarket(
					selectedUiMarket.marketId.key,
					userKey ?? ''
				) ?? 'cross'
		) || 'cross';

	const handlePositionSliderChange = useCallback(
		(val: number) => {
			const newValue = currentPositionBaseSize.scale(val * 100, 10000);

			// If we've slid the value down to `0`, clear the input.
			const valueToSet = newValue.eqZero() ? '' : newValue.printShort(true);

			onSizeInputChange(valueToSet, {
				// The position slider only operates on the base size.
				sizeUnit: 'base',
			});
		},
		[currentPositionBaseSize, onSizeInputChange]
	);

	return (
		<div
			className="flex h-full flex-col"
			onMouseLeave={unFocus}
			{...InvariantChecker.getInvariantCheckerContextWrapperHtmlAttributes(
				'perp-trade-form'
			)}
		>
			<div
				className={cj(
					'relative flex flex-col gap-y-md',
					BLUR_OVERLAY_SIBLING_CLASSES
				)}
			>
				<div className="flex items-center gap-x-sm">
					<Button
						variant="stroke"
						size={buttonSize}
						className="flex-1"
						onClick={openMarginModeModal}
						rightIcon={isMobileScreenSize ? ChevronDown : undefined}
					>
						{currentMarginMode === 'isolated' ? 'Isolated' : 'Cross'}
					</Button>

					<Button
						variant="stroke"
						size={buttonSize}
						className="flex-1"
						onClick={openEditPositionMaxLeverage}
						rightIcon={isMobileScreenSize ? ChevronDown : undefined}
					>
						{formatLeverageLabel(displayLeverage)}
					</Button>
				</div>

				<BuySellToggle onChange={onDirectionChange} />

				<div className="flex flex-col gap-y-lg">
					{!needMobileUI && (
						<OrderTypeTabs
							currentOrderType={tradeFormStoreState.orderType}
							orderTypeOptions={PERP_ORDER_TYPE_OPTIONS}
							onOrderTypeChange={onOrderTypeChange}
						/>
					)}
					<div className="flex flex-col gap-y-md">
						<MaxSizeLabel
							currentPositionBaseSizeString={currentPositionBaseSize.toTradePrecision()}
							freeCollateral={freeCollateralNum}
							formattedMarketSymbol={formattedMarketSymbol}
							reduceOnly={reduceOnly}
							stepSize={stepSize}
							marginMode={currentMarginMode}
						/>
						<div className="flex flex-col gap-y-sm">
							{needMobileUI && (
								<OrderTypeBottomDrawerSelect marketType={MarketType.PERP} />
							)}
							{tradeFormStoreState.orderType !== 'market' && (
								<PriceInput
									value={priceBoxStringValue}
									handleChange={onPriceBoxChange}
									handleBlur={saveLimitPrice}
									marketIndex={selectedUiMarket.marketIndex}
									marketType={MarketType.PERP}
									label={
										tradeFormStoreState.orderType === 'scaledOrders'
											? 'Start Price'
											: tradeFormDerivedState.priceBoxHeading
									}
									showMidButton={tradeFormStoreState.orderType === 'limit'}
									allowNegative={
										tradeFormStoreState.orderType === 'oracleLimit'
									}
									baseAssetSymbol={formattedMarketSymbol}
								/>
							)}
							{tradeFormDerivedState.showSecondaryPriceBox && (
								<PriceInput
									value={secondaryPriceBoxStringValue}
									handleChange={onSecondaryPriceBoxChange}
									handleBlur={saveLimitPrice}
									marketIndex={selectedUiMarket.marketIndex}
									marketType={MarketType.PERP}
									label={
										tradeFormStoreState.orderType === 'scaledOrders'
											? 'End Price'
											: 'Price'
									}
									showMidButton={tradeFormStoreState.orderType === 'stopLimit'}
									baseAssetSymbol={formattedMarketSymbol}
								/>
							)}
							<SizeInput
								baseSizeStringValue={roundToStepSizeIfLargeEnough(
									baseSizeStringValue,
									stepSize
								)}
								formattedMarketSymbol={formattedMarketSymbol}
								quoteSizeStringValue={quoteSizeStringValue}
								maxQuoteAvailable={tradeFormDerivedState.maxQuoteAvailable}
								onBaseSizeChange={onSizeInputChange}
								onQuoteSizeChange={onSizeInputChange}
								onInputSourceChange={onInputSourceChange}
								stepSize={stepSize}
								sizeErrorMessage={
									crossMarginErrorMessage ??
									tradeFormDerivedState.sizeErrorMessage?.message
								}
							/>

							{tradeFormDerivedState.showLeverageSlider && (
								<PerpLeverageSliderInput
									currentLeverage={currentLeverage}
									targetTradeQuoteSize={rawQuoteValue}
									totalCurrentPositionSize={totalCurrentPositionSize}
									targetMarketCurrentPositionSize={currentPositionSize}
									sameSide={isSameSideAsCurrentPosition}
									maxQuoteSize={tradeFormDerivedState.maxQuoteAvailable}
									maxLeverage={tradeFormDerivedState.maxLeverageAvailable}
									onCurrentSizeChange={onSliderChange}
									onMaxLeverageClick={
										tradeFormDerivedState.handleMaxLeverageClick
									}
									onLeverageChange={tradeFormDerivedState.handleFormChange}
								/>
							)}

							{tradeFormDerivedState.showReducePositionSlider && (
								<Slider
									value={Math.min(
										Math.max(
											0,
											currentPositionBaseSize.eqZero()
												? 0
												: (rawBaseValue.toNum() /
														currentPositionBaseSize.toNum()) *
														100
										),
										100
									)}
									onValueChange={handlePositionSliderChange}
									onValueCommit={handlePositionSliderChange}
								/>
							)}
						</div>
					</div>

					{tradeFormDerivedState.showOrdersSplit && (
						<>
							{!needMobileUI ? (
								<>
									<ScaledOrderCountInput />
									<div className="flex flex-col gap-y-sm">
										<DefinitionTooltip
											className="w-fit typo-b5 text-text-secondary md:typo-b4"
											definitionId="tradingScaledOrderSizeDistribution"
										>
											Size Distribution
										</DefinitionTooltip>
										<RadioGroup.Default
											size="md"
											variant="card"
											className="grow gap-x-xs"
											value={sizeDistribution}
											onValueChange={onSizeDistributionChange}
											items={[
												{
													value: 'flat',
													id: 'flat',
													label: 'Flat',
													className: 'flex-1',
												},
												{
													value: 'ascending',
													id: 'ascending',
													label: 'Ascending',
													className: 'flex-1',
												},
												{
													value: 'descending',
													id: 'descending',
													label: 'Descending',
													className: 'flex-1',
												},
											]}
										/>
									</div>
								</>
							) : (
								<div className="flex flex-col gap-y-sm">
									<ScaledOrderCountInput />
									<BottomDrawerSelectSizeDistribution />
								</div>
							)}
						</>
					)}

					<div className="grid grid-cols-2 gap-y-md">
						{tradeFormDerivedState.showReduceOnly && (
							<Checkbox.Default
								id="pro-mode-perp-trade-form-reduce-only"
								label="Reduce Only"
								checked={reduceOnly}
								onCheckedChange={toggleReduceOnly}
								tooltip={{ definitionId: 'tradingReduceOnly' }}
								disabled={tradeFormDerivedState.isReduceOnlyDisabled}
								className="w-fit"
							/>
						)}
						{tradeFormDerivedState.showPostOnly && (
							<div className="flex justify-end">
								<Checkbox.Default
									id="pro-mode-perp-trade-form-post-only"
									label="Post Only"
									checked={postOnly}
									onCheckedChange={togglePostOnly}
									tooltip={{ definitionId: 'tradingPostOnly' }}
									// Note, using wrappers around the checkbox's + `w-fit` so the clickable
									// area is constrained to the checkbox + label.
									className="w-fit"
								/>
							</div>
						)}
						{shouldShowTpSlCheckbox && (
							<div className="col-span-2">
								<Checkbox.Default
									id="pro-mode-perp-trade-form-tp-sl"
									label="TP/SL"
									checked={tradeFormStoreState.isTpSlCheckboxChecked}
									onCheckedChange={toggleIsTpSlCheckboxChecked}
									disabled={reduceOnly}
									className="w-fit"
								/>
								<CollapsibleTpSlOrderForm
									isTpSlCheckboxChecked={
										tradeFormStoreState.isTpSlCheckboxChecked
									}
									side={tradeFormStoreState.side}
									entryPrice={entryPriceForTpSl}
									entrySize={baseSizeBigNum}
									saveBracketOrdersCallback={saveBracketOrdersCallback}
									orderType={tradeFormStoreState.orderType}
									bracketOrders={tradeFormStoreState.bracketOrders}
								/>
							</div>
						)}
					</div>
				</div>
			</div>

			{shouldShowSwiftEnableMessage && (
				<div className={needMobileUI ? 'pt-md' : 'pt-lg'}>
					<SwiftEnableMessage />
				</div>
			)}

			{/* Messages that should appear above the primary button */}
			{tradeFormDerivedState.shouldShowMessage &&
				tradeFormDerivedState.messagePosition === 'above-button' && (
					<div className="pt-2 pb-1">
						<InlineAlert
							variant={tradeFormDerivedState.messageType}
							title={tradeFormDerivedState.messageTitle}
						>
							{tradeFormDerivedState.message && (
								<span>{tradeFormDerivedState.message}</span>
							)}

							{tradeFormDerivedState.messageLink && (
								<Link href={tradeFormDerivedState.messageLink} openInNewTab>
									{tradeFormDerivedState.messageLinkDescription}
								</Link>
							)}
						</InlineAlert>
					</div>
				)}

			<div
				className={cj(
					'pb-md',
					tradeFormDerivedState.shouldShowMessage &&
						tradeFormDerivedState.messagePosition === 'above-button'
						? 'pt-1'
						: needMobileUI
							? 'pt-md'
							: 'pt-2xl'
				)}
			>
				<PerpTradeFormPrimaryButton
					side={side}
					isMarketInSettlement={!!tradeFormDerivedState.marketInSettlement}
					currentMarketSymbol={currentMarketSymbol}
					onSubmit={onSubmit}
					hasNoCollateral={hasNoCollateral}
					disabled={isTradeButtonDisabled}
				/>
			</div>

			<div
				className={cj('flex flex-col', needMobileUI ? 'gap-y-md' : 'gap-y-lg')}
			>
				<PerpTradeDetails
					marginRequired={totalMarginRequired}
					orderType={tradeFormStoreState.orderType}
					showSlippageTolerance={tradeFormDerivedState.showSlippageTolerance}
				/>

				{tradeFormDerivedState.showPriceEstimateOracleDivergenceWarning &&
					!showTradeConfirmation && (
						<PriceDivergenceWarningMessage
							hasAgreedToPriceDivergence={hasAgreedToPriceDivergence}
							setHasAgreedToPriceDivergence={setHasAgreedToPriceDivergence}
						/>
					)}

				{!!limitPriceVsTriggerPriceWarning && (
					<InlineAlert
						variant="warning"
						title={limitPriceVsTriggerPriceWarning}
					/>
				)}

				{tradeFormDerivedState.positionSizeLimitExceeded?.shouldShowMessage && (
					<InlineAlert
						variant={
							tradeFormDerivedState.positionSizeLimitExceeded.messageType
						}
						title={tradeFormDerivedState.positionSizeLimitExceeded.messageTitle}
					>
						{tradeFormDerivedState.positionSizeLimitExceeded.message}
					</InlineAlert>
				)}

				{tradeFormDerivedState.shouldShowMessage &&
					tradeFormDerivedState.messagePosition !== 'above-button' && (
						<InlineAlert
							variant={tradeFormDerivedState.messageType}
							title={tradeFormDerivedState.messageTitle}
						>
							{tradeFormDerivedState.message && (
								<span>{tradeFormDerivedState.message}</span>
							)}
							{tradeFormDerivedState.messageLink && (
								<Link href={tradeFormDerivedState.messageLink} openInNewTab>
									{tradeFormDerivedState.messageLinkDescription}
								</Link>
							)}
						</InlineAlert>
					)}

				<ApyBanners />
			</div>
		</div>
	);
};
